import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Trash2, Loader, BookOpen, ChevronRight, ChevronDown, ExternalLink, Folder, FolderOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Языковые настройки
const LANGUAGES = {
  ru: { name: 'Русский', flag: '🇷🇺' },
  en: { name: 'English', flag: '🇬🇧' },
  kk: { name: 'Қазақша', flag: '🇰🇿' }
};

// Алфавиты для разных языков
const ALPHABETS = {
  ru: ['А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ж', 'З', 'И', 'К', 'Л', 'М', 'Н', 'О', 'П', 'Р', 'С', 'Т', 'У', 'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Щ', 'Э', 'Ю', 'Я'],
  en: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'],
  kk: ['А', 'Ә', 'Б', 'В', 'Г', 'Ғ', 'Д', 'Е', 'Ж', 'З', 'И', 'Й', 'К', 'Қ', 'Л', 'М', 'Н', 'Ң', 'О', 'Ө', 'П', 'Р', 'С', 'Т', 'У', 'Ұ', 'Ү', 'Ф', 'Х', 'Һ', 'Ц', 'Ч', 'Ш', 'Щ', 'Ы', 'І', 'Э', 'Ю', 'Я']
};

// Переводы интерфейса
const UI_TRANSLATIONS = {
  ru: {
    title: 'Военный тезаурус',
    subtitle: 'База военной терминологии, синонимов и понятий',
    alphabetical: 'Алфавитное представление',
    hierarchical: 'Иерархическое представление',
    all: 'Все',
    searchPlaceholder: 'Поиск по терминам, синонимам, описанию...',
    search: 'Поиск',
    synonyms: 'Синонимы',
    noTerms: 'Термины не найдены',
    noCategories: 'Категории не найдены',
    loading: 'Загрузка...',
    confirmDelete: 'Вы уверены, что хотите удалить этот термин?',
    termsCount: 'терминов',
    otherTerms: 'Другие термины'
  },
  en: {
    title: 'Military Thesaurus',
    subtitle: 'Database of military terminology, synonyms and concepts',
    alphabetical: 'Alphabetical view',
    hierarchical: 'Hierarchical view',
    all: 'All',
    searchPlaceholder: 'Search by terms, synonyms, description...',
    search: 'Search',
    synonyms: 'Synonyms',
    noTerms: 'No terms found',
    noCategories: 'No categories found',
    loading: 'Loading...',
    confirmDelete: 'Are you sure you want to delete this term?',
    termsCount: 'terms',
    otherTerms: 'Other terms'
  },
  kk: {
    title: 'Әскери тезаурус',
    subtitle: 'Әскери терминология, синонимдер және ұғымдар базасы',
    alphabetical: 'Әліпбилік көрініс',
    hierarchical: 'Иерархиялық көрініс',
    all: 'Барлығы',
    searchPlaceholder: 'Терминдер, синонимдер, сипаттама бойынша іздеу...',
    search: 'Іздеу',
    synonyms: 'Синонимдер',
    noTerms: 'Терминдер табылмады',
    noCategories: 'Санаттар табылмады',
    loading: 'Жүктелуде...',
    confirmDelete: 'Бұл терминді жоюға сенімдісіз бе?',
    termsCount: 'терминдер',
    otherTerms: 'Басқа терминдер'
  }
};

// Вспомогательные функции
const getTermNameUtil = (term, language) => {
  if (term.pt && term.pt[language]) {
    return term.pt[language];
  }
  if (term.pt && term.pt.ru) {
    return term.pt.ru;
  }
  return term.term;
};

const getDescriptionUtil = (term, language) => {
  if (term.definition && term.definition[language]) {
    return term.definition[language];
  }
  if (term.definition && term.definition.ru) {
    return term.definition.ru;
  }
  return term.description;
};

// Получить название категории на выбранном языке
const getCategoryName = (category, language) => {
  if (typeof category === 'object') {
    if (language === 'en' && category.name_en) return category.name_en;
    if (language === 'kk' && category.name_kk) return category.name_kk;
    return category.name;
  }
  return category;
};

// Компонент для элемента термина
const TermItem = ({ term, handleDelete, language, t, canDelete = false }) => {
  const termName = getTermNameUtil(term, language);
  const description = getDescriptionUtil(term, language);

  return (
    <div className="flex items-start gap-2 p-3 hover:bg-gray-50 rounded-lg transition-colors ml-4 border-l-2 border-blue-200">
      <div className="w-5 h-5 flex items-center justify-center mt-1">
        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
      </div>
      
      <div className="flex-1">
        <Link
          to={`/concept/${term.id}`}
          className="text-base font-semibold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2"
        >
          {termName}
          <ExternalLink className="w-3 h-3" />
        </Link>
        {description && (
          <p className="text-gray-600 text-sm mt-1 line-clamp-1">{description}</p>
        )}
      </div>
      
      {canDelete && (
        <button
          onClick={() => handleDelete(term.id)}
          className="text-red-500 hover:text-red-700 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Компонент BT-ветки внутри категории
const BTBranch = ({ btName, terms, allTerms, expanded, onToggle, handleDelete, language, t, canDelete = false }) => {
  const branchTerms = terms.filter(term => 
    term.broader_terms && term.broader_terms.includes(btName)
  );

  if (branchTerms.length === 0) return null;

  // Найти термин с таким именем для ссылки
  const btTerm = allTerms.find(term => 
    term.term === btName || 
    (term.pt && (term.pt.ru === btName || term.pt.en === btName || term.pt.kk === btName))
  );

  return (
    <div className="ml-4 mb-2">
      <div className="flex items-center gap-2 p-3 hover:bg-blue-50 rounded-lg transition-colors">
        <button
          onClick={onToggle}
          className="flex-shrink-0"
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-blue-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-blue-600" />
          )}
        </button>
        
        {btTerm ? (
          <Link
            to={`/concept/${btTerm.id}`}
            className="text-base font-medium text-blue-600 hover:text-blue-800 hover:underline"
          >
            {btName}
          </Link>
        ) : (
          <span className="text-base font-medium text-gray-800">{btName}</span>
        )}
        <span className="text-sm text-gray-500">({branchTerms.length})</span>
      </div>
      
      {expanded && (
        <div className="mt-1 space-y-1">
          {branchTerms.map(term => (
            <TermItem
              key={term.id}
              term={term}
              handleDelete={handleDelete}
              language={language}
              t={t}
              canDelete={canDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Компонент категории для иерархического вида
const CategoryItem = ({ category, terms, expandedItems, toggleExpand, handleDelete, language, t, canDelete = false }) => {
  const categoryName = typeof category === 'object' ? category.name : category;
  const categoryId = typeof category === 'object' ? category.id : null;
  const categoryTerms = terms.filter(term => term.category === categoryName);
  const isExpanded = expandedItems[`cat_${categoryName}`];
  const displayName = getCategoryName(category, language);

  // Собрать все уникальные BT из терминов этой категории
  const allBTs = new Set();
  categoryTerms.forEach(term => {
    if (term.broader_terms && term.broader_terms.length > 0) {
      term.broader_terms.forEach(bt => allBTs.add(bt));
    }
  });
  const btList = Array.from(allBTs).sort();

  // Термины без BT
  const termsWithoutBT = categoryTerms.filter(term => 
    !term.broader_terms || term.broader_terms.length === 0
  );

  return (
    <div className="mb-2">
      <div className="flex items-center gap-3 p-4 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors">
        <button
          onClick={() => toggleExpand(`cat_${categoryName}`)}
          className="flex-shrink-0"
        >
          {isExpanded ? (
            <FolderOpen className="w-6 h-6 text-blue-600" />
          ) : (
            <Folder className="w-6 h-6 text-blue-600" />
          )}
        </button>
        
        <div className="flex-1 text-left">
          {categoryId ? (
            <Link
              to={`/category/${categoryId}`}
              className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline"
            >
              {displayName}
            </Link>
          ) : (
            <span className="text-lg font-semibold text-gray-900">{displayName}</span>
          )}
          <span className="ml-2 text-sm text-gray-500">({categoryTerms.length} {t.termsCount})</span>
        </div>
        
        <button
          onClick={() => toggleExpand(`cat_${categoryName}`)}
          className="flex-shrink-0"
        >
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-2 ml-4 bg-gray-50 rounded-lg p-3">
          {/* BT ветки */}
          {btList.map(btName => (
            <BTBranch
              key={btName}
              btName={btName}
              terms={categoryTerms}
              allTerms={terms}
              expanded={expandedItems[`bt_${categoryName}_${btName}`]}
              onToggle={() => toggleExpand(`bt_${categoryName}_${btName}`)}
              handleDelete={handleDelete}
              language={language}
              t={t}
              canDelete={canDelete}
            />
          ))}
          
          {/* Термины без BT */}
          {termsWithoutBT.length > 0 && (
            <div className="ml-4">
              {btList.length > 0 && (
                <div className="text-sm font-medium text-gray-500 mb-2 mt-3">
                  {t.otherTerms}:
                </div>
              )}
              <div className="space-y-1">
                {termsWithoutBT.map(term => (
                  <TermItem
                    key={term.id}
                    term={term}
                    handleDelete={handleDelete}
                    language={language}
                    t={t}
                    canDelete={canDelete}
                  />
                ))}
              </div>
            </div>
          )}
          
          {categoryTerms.length === 0 && (
            <div className="text-gray-500 text-sm p-3">
              {t.noTerms}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ThesaurusPageNew = () => {
  const { isAdmin, token } = useAuth();
  const [terms, setTerms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [viewMode, setViewMode] = useState('alphabetical');
  const [language, setLanguage] = useState('ru');
  const [expandedItems, setExpandedItems] = useState({});

  // Получаем переводы для текущего языка
  const t = UI_TRANSLATIONS[language];
  const alphabet = ALPHABETS[language];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [termsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API}/thesaurus`),
        axios.get(`${API}/categories`)
      ]);
      setTerms(termsResponse.data);
      setCategories(categoriesResponse.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchData();
      return;
    }

    try {
      const response = await axios.get(`${API}/thesaurus/search`, {
        params: { q: searchQuery }
      });
      setTerms(response.data);
    } catch (err) {
      console.error('Error searching terms:', err);
    }
  };

  const handleDelete = async (termId) => {
    if (!window.confirm(t.confirmDelete)) {
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${BACKEND_URL}/api/thesaurus/${termId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete term');
      }

      fetchData();
    } catch (err) {
      console.error('Error deleting term:', err);
      const errorMsg = language === 'ru' ? 'Ошибка: Не удалось удалить термин' : 
                      language === 'kk' ? 'Қате: Терминді жоюға болмады' :
                      'Error: Failed to delete term';
      alert(errorMsg);
    }
  };

  // Переключение раскрытия элемента
  const toggleExpand = (key) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Получить название термина на выбранном языке
  const getTermName = (term) => getTermNameUtil(term, language);

  // Фильтрация по букве
  const filterByLetter = (termsList) => {
    if (selectedLetter === 'all') return termsList;
    return termsList.filter(term => {
      const termName = getTermName(term);
      return termName.toUpperCase().startsWith(selectedLetter);
    });
  };

  // Группировка по первой букве для алфавитного вида
  const groupByLetter = (termsList) => {
    const grouped = {};
    termsList.forEach(term => {
      const termName = getTermName(term);
      const firstLetter = termName.charAt(0).toUpperCase();
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = [];
      }
      grouped[firstLetter].push(term);
    });
    return grouped;
  };

  // Получить уникальные категории из терминов + из API
  const getUniqueCategories = () => {
    const categoryNames = [...new Set(terms.map(t => t.category).filter(Boolean))];
    const apiCategoryNames = categories.map(c => c.name);
    const allNames = [...new Set([...apiCategoryNames, ...categoryNames])];
    
    return allNames.map(name => {
      const apiCat = categories.find(c => c.name === name);
      return apiCat || { name, name_en: name, name_kk: name };
    });
  };

  const filteredTerms = filterByLetter(terms);
  const groupedByLetter = groupByLetter(filteredTerms);
  const uniqueCategories = getUniqueCategories();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header with Language Switcher */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3" data-testid="thesaurus-title">
              {t.title}
            </h1>
            <p className="text-lg text-gray-600">
              {t.subtitle}
            </p>
          </div>
          
          {/* Language Switcher */}
          <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-gray-200 p-1">
            {Object.entries(LANGUAGES).map(([code, lang]) => (
              <button
                key={code}
                onClick={() => setLanguage(code)}
                className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 ${
                  language === code
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                data-testid={`lang-${code}`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="hidden sm:inline">{lang.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* View Mode Selector */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => setViewMode('alphabetical')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                viewMode === 'alphabetical'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="view-alphabetical"
            >
              {t.alphabetical}
            </button>
            <button
              onClick={() => setViewMode('hierarchical')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                viewMode === 'hierarchical'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="view-hierarchical"
            >
              {t.hierarchical}
            </button>
          </div>
        </div>

        {/* Alphabet Navigation - Only in Alphabetical View */}
        {viewMode === 'alphabetical' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedLetter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedLetter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="letter-all"
              >
                {t.all}
              </button>
              {alphabet.map((letter) => (
                <button
                  key={letter}
                  onClick={() => setSelectedLetter(letter)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    selectedLetter === letter
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`letter-${letter}`}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search - Only in Alphabetical View */}
        {viewMode === 'alphabetical' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder={t.searchPlaceholder}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <button
                onClick={handleSearch}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                data-testid="search-button"
              >
                {t.search}
              </button>
            </div>
          </div>
        )}

        {/* Terms Display */}
        {loading ? (
          <div className="text-center py-12">
            <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">{t.loading}</p>
          </div>
        ) : (
          <>
            {/* Alphabetical View */}
            {viewMode === 'alphabetical' && (
              filteredTerms.length > 0 ? (
                <div className="space-y-8">
                  {Object.entries(groupedByLetter)
                    .sort(([a], [b]) => a.localeCompare(b, language === 'en' ? 'en' : 'ru'))
                    .map(([letter, letterTerms]) => (
                      <div key={letter} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h2 className="text-3xl font-bold text-blue-600 mb-6 border-b-2 border-blue-600 pb-2">
                          {letter}
                        </h2>
                        <div className="space-y-4">
                          {letterTerms
                            .sort((a, b) => getTermName(a).localeCompare(getTermName(b), language === 'en' ? 'en' : 'ru'))
                            .map((term, index) => (
                              <div
                                key={term.id}
                                className="border-l-4 border-blue-500 pl-4 py-2 hover:bg-gray-50 transition-colors"
                                data-testid={`term-item-${index}`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <Link 
                                        to={`/concept/${term.id}`}
                                        className="text-xl font-bold text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-2"
                                      >
                                        {getTermName(term)}
                                        <ExternalLink className="w-4 h-4" />
                                      </Link>
                                      <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full">
                                        {term.category}
                                      </span>
                                    </div>

                                    {getDescriptionUtil(term, language) && (
                                      <p className="text-gray-700 mb-2 line-clamp-2">{getDescriptionUtil(term, language)}</p>
                                    )}

                                    {term.synonyms && term.synonyms.length > 0 && (
                                      <div className="flex items-center gap-2 text-sm">
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                        <span className="text-gray-600">{t.synonyms}:</span>
                                        <div className="flex flex-wrap gap-1">
                                          {term.synonyms.map((synonym, idx) => (
                                            <span
                                              key={idx}
                                              className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded"
                                            >
                                              {synonym}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {isAdmin && (
                                    <button
                                      onClick={() => handleDelete(term.id)}
                                      className="text-red-500 hover:text-red-700 transition-colors ml-4"
                                      data-testid={`delete-term-${index}`}
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">{t.noTerms}</p>
                </div>
              )
            )}

            {/* Hierarchical View - Categories with BT branches */}
            {viewMode === 'hierarchical' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                {uniqueCategories.length > 0 ? (
                  <div className="space-y-2">
                    {uniqueCategories.map((category) => (
                      <CategoryItem
                        key={typeof category === 'object' ? category.id || category.name : category}
                        category={category}
                        terms={terms}
                        expandedItems={expandedItems}
                        toggleExpand={toggleExpand}
                        handleDelete={handleDelete}
                        language={language}
                        t={t}
                        canDelete={isAdmin}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">{t.noCategories}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ThesaurusPageNew;
